import type { ReactNode } from 'react';
import { C, H2, Lead, Note, P, UL } from '../Doc';

function Release({
  version, date, children,
}: { version: string; date: string; children: ReactNode }) {
  return (
    <section className="border-t border-[var(--ff-border)] pt-6 first:border-0 first:pt-0">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h2 className="text-[17px] font-semibold tracking-[-0.01em]">{version}</h2>
        <span className="ff-num text-[13px] text-[var(--ff-text-faint)]">{date}</span>
      </div>
      {children}
    </section>
  );
}

function Change({ kind, children }: { kind: 'Added' | 'Changed' | 'Fixed'; children: ReactNode }) {
  const tone = {
    Added: 'text-[var(--ff-success)]',
    Changed: 'text-[var(--ff-primary)]',
    Fixed: 'text-[var(--ff-select)]',
  }[kind];

  return (
    <li>
      <span className={`ff-mono mr-2 text-[11.5px] font-semibold uppercase ${tone}`}>{kind}</span>
      {children}
    </li>
  );
}

export function Changelog() {
  return (
    <>
      <Lead>
        Notable changes to FormForge. Entries are written by hand as work lands; the
        phase-level picture of what is built lives in <C>docs/STATUS.md</C>.
      </Lead>

      <Note title="How this page is maintained">
        This working copy has no version-control history to generate a changelog from, so
        nothing here is inferred — each entry records a change that is verifiably in the
        code. Add to the newest section as you go, and start a new one when you tag a
        release. The file is <C>apps/studio/src/help/topics/Changelog.tsx</C>.
      </Note>

      <div className="mt-8 space-y-8">
        <Release version="Unreleased" date="9 September 2026">
          <P>Current build. Not yet tagged as a release.</P>
          <ul className="mt-3 max-w-[68ch] space-y-2 leading-relaxed">
            <Change kind="Added">
              Form-type filter on the forms list, held in the URL as <C>?type=</C> and
              combining with the folder, company and text filters. The API accepts any type
              its enum knows, so forms created through the API remain reachable.
            </Change>
            <Change kind="Added">
              In-application documentation — this Help section: application and developer
              documentation, setup guide, FAQ, changelog and licensing.
            </Change>
            <Change kind="Added">
              Seven-day response sparklines on the forms list, from a new{' '}
              <C>recent</C> series on <C>/v1/forms</C>. Read from raw rows over a bounded
              window rather than the daily rollup, which lags its worker.
            </Change>
            <Change kind="Added">
              Command palette (<C>⌘K</C>) for searching forms, jumping between screens and
              switching company; and an offline banner that names when the API stopped
              answering and says the visible rows are the last successful sync.
            </Change>
            <Change kind="Changed">
              New console design applied through the token layer, so every screen inherits
              it. Light mode uses neutral greys and carries the brand in the accent alone;
              tinting the greys toward the palette turned large empty areas sepia.
            </Change>
            <Change kind="Changed">
              Folders moved from a second rail inside the forms page into the application
              rail, with the selection in the URL so it is visible and settable from any
              screen.
            </Change>
            <Change kind="Changed">
              The runtime now reads the form type and adapts its wording — a poll says "Cast
              your vote", an application "Submit application" — with per-type review copy and
              a type label above the title. An author's own translation still wins.
            </Change>
            <Change kind="Fixed">
              Switching company had no effect. The selection was held in per-component state,
              so the switcher updated only its own copy and the forms list kept whichever
              company it mounted with. It is now one shared store, and switching navigates to
              that company's forms.
            </Change>
            <Change kind="Fixed">
              Every company card listed every workspace in the tenant. The studio read a{' '}
              <C>publicId</C> field the API does not send, so the company id was undefined and
              the request went out unfiltered.
            </Change>
            <Change kind="Fixed">
              The forms list ignored the selected company. <C>/v1/forms</C> now accepts{' '}
              <C>companyId</C>; before this the switcher changed the header and nothing else.
            </Change>
            <Change kind="Fixed">
              The builder scrolled as one page instead of three columns, so a drag started in
              the palette scrolled out from under the pointer. The grid row was content-sized
              and grew past its container.
            </Change>
            <Change kind="Fixed">
              A translated form showed English "Submitting…" mid-submit — the string was
              hardcoded rather than looked up.
            </Change>
            <Change kind="Fixed">
              The account menu ignored Escape and outside clicks, and announced itself as a
              disclosure widget. It is now a proper menu that returns focus to its trigger.
            </Change>
          </ul>
        </Release>
      </div>

      <H2 id="before">Before this</H2>
      <P>
        Earlier work is not reconstructed here, because inventing dates for it would make
        this page less trustworthy rather than more complete. For where the implementation
        stands against the product requirements — including the gaps that look finished and
        are not — read:
      </P>
      <UL>
        <li><C>docs/STATUS.md</C> — phase completion, test coverage, and the silent-gaps table.</li>
        <li><C>docs/22-ADR-Log.md</C> — the decisions taken and why, which is the closest thing to a history of the design.</li>
        <li><C>README.md</C> — what exists today, and the suites that back each claim.</li>
      </UL>
    </>
  );
}
